import {
  memo,
  useState,
  useReducer,
  useEffect,
  ChangeEvent,
  Dispatch,
  SetStateAction,
} from 'react';

import {
  freqSplit,
  getCenterWithoutLo,
  formatFreqPretty,
} from '../../utils/FreqUtils';
import { updateAnalyzerProperties } from '../spectrasWebSocketServer/spectrasWebSocketServerSlice';

import {
  Button,
  MenuItem,
  Grid,
  InputLabel,
  Select,
  TextField,
  InputAdornment,
  FormControl,
  SelectChangeEvent,
  FormHelperText,
} from '@mui/material';

import ActiveSnapshotSelect from './ActiveSnapshotSelect';
import AnalyzerModeBar from '../analyzers/AnalyzerModeBar';
import RecordButton from '../snapshotHistorySettings/RecordButton';
import FrequencySelect from 'components/FrequencySelect';

import { SnapshotValidation } from 'types';

import { useAppDispatch, useAppSelector } from '../../app/hooks';
import snapshotSettingsReducer from '../analyzers/utils/snapshotSettingsReducer';
import { SnapshotTraceHistoryCombination } from '../analyzers/types';
import SnapshotSaveButton from './SnapshotSaveButton';
import { useIsAdmin } from '../auth/hooks/useIsAdmin';

import { getValidatePropertyErrors } from '../analyzers/utils';
import { getAnalyzerType } from 'utils/analyzerTypes/index';

import { useAnalyzer } from '../analyzers/api/getAnalyzer';
import { useSwitches } from 'features/switches/api/getSwitches';
import { useUpdateSnapshot } from 'features/snapshots/api/updateSnapshot';
import { cleanUpSweepTimeResponse } from '../analyzers/utils/analyzerInputProcessing';
import { AnalyzerType } from '../../types/models/Analyzer';
import { queryClient } from '../../lib/react-query';
import VbworAverageInput from './VbwOrAverageInput';
import RbwInput from './RbwInput';
import AnalyzerPortWithLabelSelector from 'features/analyzerPortLabels/AnalyzerPortWithLabelSelector';

interface Props {
  selectedAnalyzerId: number;
  selectedSnapshot: SnapshotTraceHistoryCombination;
  setSelectedSnapshotId?: Dispatch<SetStateAction<number>>;
  displayActiveSnapshotSelector?: boolean;
  editDisabled: boolean;
  currentDialog?: string;
  inDialog?: boolean;
  inHistoryMode?: boolean;
  validatedSnapshotProperties?: SnapshotValidation | undefined | null;
  setValidatedSnapshotProperties?: Dispatch<
    SetStateAction<SnapshotValidation | undefined | null>
  >;
}
const AnalyzerSnapshotsSettings = memo(
  ({
    selectedAnalyzerId,
    selectedSnapshot,
    setSelectedSnapshotId,
    editDisabled,
    displayActiveSnapshotSelector,
    currentDialog,
    inDialog,
    inHistoryMode,
    validatedSnapshotProperties,
    setValidatedSnapshotProperties,
  }: Props) => {
    const dispatch = useAppDispatch();
    const isAdmin = useIsAdmin();
    const message = useAppSelector(
      (state) => state.spectrasWebSocketServer.message[selectedSnapshot?.id]
    );
    const { user } = useAppSelector((state) => state.auth);
    const { data: selectedAnalyzer } = useAnalyzer({
      analyzerId: selectedAnalyzerId || -1,
    });

    const { data: switches } = useSwitches({});

    const updateSnapshotMutation = useUpdateSnapshot({});
    const analyzerPropertyConstants = getAnalyzerType(
      selectedAnalyzer?.type || 0
    );

    const [editedSnapshot, dispatchLocal] = useReducer(
      snapshotSettingsReducer,
      selectedSnapshot
    );
    const [snapshotEdited, setSnapshotEdited] = useState(false);

    const validatedPropertyErrors = getValidatePropertyErrors(
      selectedSnapshot,
      validatedSnapshotProperties,
      editedSnapshot.needs_validated,
      inHistoryMode
    );
    const disableErrorBox = selectedAnalyzer?.type === 4;

    useEffect(() => {
      if (!selectedSnapshot) return;

      dispatchLocal({
        type: 'snapshot-changed',
        snapshot: selectedSnapshot,
      });
    }, [selectedSnapshot, selectedAnalyzer]);

    useEffect(() => {
      if (!message) return;
      if (message.type === 'reported_sweep_time') {
        dispatchLocal({
          type: 'property-changed',
          name: 'last_reported_sweep_time',
          value: message.message,
        });
        queryClient.invalidateQueries(['snapshots', selectedAnalyzerId]);
      } else if (message.type === 'validated_snapshot') {
        if (setValidatedSnapshotProperties) {
          setValidatedSnapshotProperties(
            message.message as unknown as SnapshotValidation
          );

          dispatchLocal({
            type: 'property-changed',
            name: 'needs_validated',
            value: false,
          });
        }
      }
      //eslint-disable-next-line
    }, [message, setValidatedSnapshotProperties]);

    const handleChange = (event: any) => {
      let { name, value } = event.target;
      const isEmpty = value === '';
      setSnapshotEdited(true);

      if (name === 'switch_port') {
        const switchAnalyzerPair = selectedAnalyzer?.analyzer_switch.find(
          (analyzerSwitch) =>
            analyzerSwitch.analyzer_id === selectedAnalyzerId &&
            Number(analyzerSwitch.switch_channel) ===
              Number(editedSnapshot.port)
        );
        const associatedSwitch = switchAnalyzerPair
          ? switches?.find(
              (analyzerSwitch) =>
                analyzerSwitch.id === switchAnalyzerPair.switch_id
            )
          : undefined;
        const switchMax = associatedSwitch?.number_of_ports;

        if (
          switchMax === null ||
          switchMax === undefined ||
          Number(value) < 1
        ) {
          value = 1;
        }

        if (
          switchMax !== undefined &&
          switchMax !== null &&
          Number(value) > switchMax
        ) {
          value = switchMax;
        }
      }

      if (isEmpty) value = '';
      dispatchLocal({
        type: 'property-changed',
        name: name,
        value: value,
      });
    };

    const handleRbwTextInputChange = (rbw: string) => {
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'property-changed',
        name: 'rbw',
        value: rbw,
      });
    };

    const handleChangePort = (
      event: SelectChangeEvent<number>,
      child: React.ReactNode
    ) => {
      const { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'port-changed',
        value: value,
      });
    };

    const handleChangeCenter = (event: ChangeEvent<HTMLInputElement>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'center-value-changed',
        value: value,
      });
    };

    const handleChangeCenterUnit = (event: SelectChangeEvent<string>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'center-unit-changed',
        value: value,
      });
    };

    const handleChangeSpan = (event: ChangeEvent<HTMLInputElement>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'span-value-changed',
        value: value,
      });
    };

    const handleChangeSpanUnit = (event: SelectChangeEvent<string>) => {
      let { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'span-unit-changed',
        value: value,
      });
    };

    const handleChangeScale = (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'scale-changed',
        value: value,
      });
    };

    const handleChangeSweepMode = (event: any) => {
      const { value } = event.target;
      setSnapshotEdited(true);
      dispatchLocal({
        type: 'sweep-mode-changed',
        value: value,
      });
    };

    useEffect(() => {
      if (!message) return;
      if (message.type === 'reported_sweep_time') {
        dispatchLocal({
          type: 'property-changed',
          name: 'last_reported_sweep_time',
          value: message.message,
        });
      }
    }, [message]);

    const handleSubmit = async (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();

      if (!snapshotEdited) return;

      queryClient
        .invalidateQueries(['dishes', 'snapshots', editedSnapshot.id])
        .then();
      const centerWithoutLo = getCenterWithoutLo(
        editedSnapshot.center,
        editedSnapshot.lo
      );

      const editedSnapshotWithoutLo = {
        ...editedSnapshot,
        center: centerWithoutLo,
        preamp: Number(editedSnapshot.preamp),
        extra_wait_time: Number(editedSnapshot.extra_wait_time),
        needs_validated: true,
        reference: Number(editedSnapshot.reference),
      };

      if (editedSnapshot.center.startsWith(' '))
        editedSnapshotWithoutLo.center = selectedSnapshot.center;
      if (editedSnapshot.span.startsWith(' '))
        editedSnapshotWithoutLo.span = selectedSnapshot.span;
      // @ts-ignore
      if (editedSnapshot.reference === '')
        editedSnapshotWithoutLo.reference = selectedSnapshot.reference;

      const { baseline_trace, ...editedSnapshotWithoutLoOrBaseline } =
        editedSnapshotWithoutLo;

      await updateSnapshotMutation.mutateAsync({
        data: editedSnapshotWithoutLoOrBaseline,
        snapshotId: editedSnapshotWithoutLoOrBaseline.id,
      });
      // Also update accompanying crosspol snapshot, if any, leaving port unchanged
      if (editedSnapshot.crosspol_snapshot_id) {
        await updateSnapshotMutation.mutateAsync({
          data: {
            ...editedSnapshotWithoutLoOrBaseline,
            port: undefined,
            crosspol_snapshot_id: undefined,
          },
          snapshotId: editedSnapshot.crosspol_snapshot_id,
        });
      }

      if (
        user &&
        selectedAnalyzer &&
        selectedAnalyzer.mode === 0 &&
        editedSnapshot.id === selectedAnalyzer.active_snapshot
      ) {
        dispatch(
          updateAnalyzerProperties({
            user_id: user.id,
            properties: {
              ...editedSnapshotWithoutLo,
              has_invalid_properties:
                validatedPropertyErrors.hasInvalidProperties,
            },
            analyzer: selectedAnalyzer,
          })
        );

        dispatchLocal({
          type: 'property-changed',
          name: 'needs_validated',
          value: true,
        });
      }

      setSnapshotEdited(false);
    };

const showSwitchInput = !inHistoryMode &&
    selectedAnalyzer?.analyzer_switch?.some(
        (analyzerSwitch) =>
            analyzerSwitch.switch_channel === Number(editedSnapshot.port)
    );
const switchPortValue = selectedAnalyzer?.analyzer_switch?.find((analyzerSwitch) => analyzerSwitch.switch_channel === Number(editedSnapshot.port))?.switch_output;
const showSwitchOutput = showSwitchInput && switchPortValue;

    return (
      <Grid
        container
        spacing={1}
        style={currentDialog !== 'marker' ? {} : { display: 'none' }}
      >
        <Grid item container spacing={1} wrap='wrap'>
          {!editDisabled && (
            <Grid item xs={3} style={{ minWidth: '108px' }}>
              <Button
                type='submit'
                variant='outlined'
                color='primary'
                onClick={handleSubmit}
                disabled={
                  !editedSnapshot?.id || editDisabled || !snapshotEdited
                }
                fullWidth
              >
                Submit
              </Button>
            </Grid>
          )}
          {displayActiveSnapshotSelector &&
            selectedAnalyzer &&
            !selectedSnapshot.crosspol_snapshot_id && (
              <Grid item xs={4} style={{ minWidth: '180px' }}>
                <RecordButton
                  analyzerId={selectedAnalyzer.id}
                  snapshotId={selectedSnapshot.id}
                />
              </Grid>
            )}
          {(displayActiveSnapshotSelector || inDialog) &&
            selectedAnalyzer &&
            user &&
            isAdmin && (
              <SnapshotSaveButton
                selectedSnapshot={selectedSnapshot}
                setSelectedSnapshotId={setSelectedSnapshotId}
                selectedAnalyzer={selectedAnalyzer}
                userId={user.id}
                inDialog={inDialog}
              />
            )}
        </Grid>
        {(displayActiveSnapshotSelector || inDialog) && selectedAnalyzer && (
          <Grid item style={{ minWidth: '10rem' }}>
            <AnalyzerModeBar
              selectedAnalyzerId={selectedAnalyzer.id}
              activeSnapshotId={editedSnapshot?.id}
            />
          </Grid>
        )}
        {displayActiveSnapshotSelector && (
          <Grid item xs={10}>
            <ActiveSnapshotSelect disabled={editDisabled} />
          </Grid>
        )}
        <Grid item xs={10}>
          {!inHistoryMode && (
            <TextField
              variant='outlined'
              required
              fullWidth
              placeholder='Name'
              id='name'
              label='Name'
              name='name'
              size='small'
              value={editedSnapshot?.name || ''}
              onChange={handleChange}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          )}
        </Grid>
        <Grid item xs={2} />
        <Grid item xs={6} style={{ minWidth: '108px' }}>
          <AnalyzerPortWithLabelSelector
            analyzerId={selectedAnalyzerId}
            selectedPort={editedSnapshot?.port}
            numberOfPorts={selectedAnalyzer?.number_of_ports}
            error={validatedPropertyErrors.portError && !disableErrorBox}
            disabled={!editedSnapshot?.id || editDisabled}
            helperText={
              validatedPropertyErrors.portError && validatedSnapshotProperties
                ? validatedSnapshotProperties.port
                : ''
            }
            onChange={handleChangePort}
          />
        </Grid>
        <Grid item xs={2} style={{ minWidth: '108px' }}>
          {showSwitchInput && (
              <TextField
                variant='outlined'
                required
                fullWidth
                id='switch_port'
                label='Switch Input'
                name='switch_port'
                autoComplete='switch_port'
                size='small'
                type='number'
                inputProps={{
                  min: '1',
                  step: '1',
                }}
                value={editedSnapshot?.switch_port || ''}
                onChange={handleChange}
                disabled={!editedSnapshot?.id || editDisabled}
              />
          )}
        </Grid>
        <Grid item xs={2} style={{ minWidth: '108px' }}>
          {showSwitchOutput && (
              <TextField
                variant='outlined'
                fullWidth
                id='switch_output'
                label='Switch Output'
                name='switch_output'
                size='small'
                type='number'
                value={switchPortValue}
                disabled={true}
              />
          )}
        </Grid>
        <Grid item container spacing={1}>
          <Grid item xs={4} style={{ minWidth: '108px' }}>
            <TextField
              variant='outlined'
              required
              fullWidth
              type='number'
              inputProps={{ min: '1', step: '1' }}
              error={validatedPropertyErrors.centerError && !disableErrorBox}
              helperText={
                validatedPropertyErrors.centerError &&
                validatedSnapshotProperties &&
                formatFreqPretty(validatedSnapshotProperties.center)
              }
              size='small'
              id='center'
              label='Center'
              name='centerValue'
              autoComplete='center'
              value={
                isNaN(parseFloat(freqSplit(editedSnapshot?.center)[0]))
                  ? ''
                  : Math.abs(parseFloat(freqSplit(editedSnapshot?.center)[0]))
              }
              onChange={handleChangeCenter}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
          <Grid item xs={6} style={{ minWidth: '108px' }}>
            <FrequencySelect
              selected={freqSplit(editedSnapshot?.center)[1] || ''}
              name='centerUnit'
              onChange={handleChangeCenterUnit}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
        </Grid>
        <Grid item xs={4} style={{ minWidth: '108px' }}>
          <TextField
            variant='outlined'
            required
            fullWidth
            type='number'
            size='small'
            id='span'
            label='Span'
            name='spanValue'
            autoComplete='span'
            error={validatedPropertyErrors.spanError && !disableErrorBox}
            helperText={
              validatedPropertyErrors.spanError &&
              validatedSnapshotProperties &&
              formatFreqPretty(validatedSnapshotProperties.span)
            }
            value={
              isNaN(parseFloat(freqSplit(editedSnapshot?.span)[0]))
                ? ''
                : Math.abs(parseFloat(freqSplit(editedSnapshot?.span)[0]))
            }
            onChange={handleChangeSpan}
            disabled={!editedSnapshot?.id || editDisabled}
          />
        </Grid>
        <Grid item xs={6} style={{ minWidth: '108px' }}>
          <FrequencySelect
            selected={freqSplit(editedSnapshot?.span)[1] || ''}
            name='spanUnit'
            onChange={handleChangeSpanUnit}
            disabled={!editedSnapshot?.id || editDisabled}
          />
        </Grid>
        <Grid container item spacing={1}>
          <Grid item xs={4} style={{ minWidth: '140px' }}>
            <TextField
              variant='outlined'
              required
              type='number'
              id='reference'
              label='Reference'
              name='reference'
              autoComplete='reference'
              size='small'
              error={validatedPropertyErrors.referenceError && !disableErrorBox}
              helperText={
                validatedPropertyErrors.referenceError &&
                validatedSnapshotProperties?.reference
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>dBm</InputAdornment>
                ),
              }}
              value={
                editedSnapshot?.reference === null ||
                editedSnapshot?.reference === undefined
                  ? ''
                  : editedSnapshot.reference
              }
              onChange={handleChange}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
          <Grid item xs={4} style={{ minWidth: '140px' }}>
            <TextField
              variant='outlined'
              required
              type='number'
              id='scale'
              label='Scale'
              name='scale'
              autoComplete='scale'
              size='small'
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>dB/div</InputAdornment>
                ),
              }}
              value={editedSnapshot?.scale || ''}
              onChange={handleChangeScale}
              disabled={!editedSnapshot?.id || editDisabled}
            />
          </Grid>
        </Grid>
        <Grid container item spacing={1}>
          <Grid item xs={4} style={{ minWidth: '108px' }}>
            <RbwInput
              analyzer={selectedAnalyzer}
              analyzerPropertyConstants={analyzerPropertyConstants}
              selectedSnapshot={selectedSnapshot}
              editedSnapshot={editedSnapshot}
              validatedSnapshotProperties={validatedSnapshotProperties}
              validatedPropertyErrors={validatedPropertyErrors}
              editDisabled={editDisabled}
              disableErrorBox={disableErrorBox}
              handleRbwTextInputChange={handleRbwTextInputChange}
              handleChange={handleChange}
            />
          </Grid>
          <Grid item xs={4} style={{ minWidth: '108px' }}>
            <VbworAverageInput
              analyzer={selectedAnalyzer}
              analyzerPropertyConstants={analyzerPropertyConstants}
              selectedSnapshot={selectedSnapshot}
              editedSnapshot={editedSnapshot}
              validatedSnapshotProperties={validatedSnapshotProperties}
              validatedPropertyErrors={validatedPropertyErrors}
              editDisabled={editDisabled}
              disableErrorBox={disableErrorBox}
              handleChange={handleChange}
            />
          </Grid>
        </Grid>
        <Grid item container spacing={1}>
          <Grid item xs={4} style={{ minWidth: '108px' }}>
            <FormControl
              variant='outlined'
              size='small'
              error={validatedPropertyErrors.attenError && !disableErrorBox}
              fullWidth
            >
              <InputLabel id='atten-label'>Atten</InputLabel>
              <Select
                labelId='atten-label'
                id='atten'
                name='atten'
                label='Atten'
                required
                fullWidth
                value={editedSnapshot?.atten || ''}
                onChange={handleChange}
                disabled={!editedSnapshot?.id || editDisabled}
              >
                {analyzerPropertyConstants?.ATTEN_VALUES.map((value) => (
                  <MenuItem value={value} key={value}>
                    {value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {(validatedPropertyErrors.attenError ||
                  selectedSnapshot.atten === 'AUTO') &&
                  validatedSnapshotProperties?.atten &&
                  validatedSnapshotProperties?.atten + ' dB'}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item xs={4} style={{ minWidth: '108px' }}>
            <FormControl
              variant='outlined'
              size='small'
              error={validatedPropertyErrors.preAmpError && !disableErrorBox}
              fullWidth
            >
              <InputLabel id='mode-label'>Preamp</InputLabel>
              <Select
                labelId='preamp-label'
                id='preamp'
                name='preamp'
                label='preamp'
                required
                fullWidth
                value={
                  editedSnapshot?.preamp === undefined
                    ? ''
                    : editedSnapshot.preamp
                }
                onChange={handleChange}
                disabled={
                  !editedSnapshot?.id ||
                  editDisabled ||
                  analyzerPropertyConstants?.PREAMP_VALUES.length === 1
                }
              >
                {analyzerPropertyConstants?.PREAMP_VALUES.map(
                  (value, index) => (
                    <MenuItem value={index} key={value}>
                      {value}
                    </MenuItem>
                  )
                )}
              </Select>
              <FormHelperText>
                {validatedPropertyErrors.preAmpError &&
                  validatedSnapshotProperties &&
                  analyzerPropertyConstants?.PREAMP_VALUES.at(
                    validatedSnapshotProperties.preamp
                  )}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid item container spacing={1}>
            <Grid item xs={4} style={{ minWidth: '108px' }}>
              <FormControl variant='outlined' size='small' fullWidth>
                <InputLabel id='sweep-time-mode-label'>Sweep Mode</InputLabel>
                <Select
                  labelId='sweep-time-mode-label'
                  id='sweep_time_mode'
                  name='sweep_time_mode'
                  label='Sweep Time'
                  required
                  fullWidth
                  value={editedSnapshot?.sweep_time_mode || 0}
                  onChange={handleChangeSweepMode}
                  disabled={
                    !editedSnapshot?.id ||
                    editDisabled ||
                    !analyzerPropertyConstants.SWEEP_TIME_ENABLED
                  }
                >
                  <MenuItem value={0} key={0}>
                    AUTO
                  </MenuItem>
                  <MenuItem value={1} key={1}>
                    MANUAL
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3} style={{ minWidth: '108px' }}>
              <TextField
                variant='outlined'
                required
                id='manual_sweep_time'
                label='Time'
                name='manual_sweep_time'
                autoComplete='scale'
                size='small'
                InputProps={{
                  endAdornment:
                    selectedAnalyzer?.type !== AnalyzerType.LPT3000R ? (
                      <InputAdornment position='end'>ms</InputAdornment>
                    ) : undefined,
                }}
                value={
                  editedSnapshot?.sweep_time_mode === 0
                    ? cleanUpSweepTimeResponse(
                        editedSnapshot?.last_reported_sweep_time,
                        selectedAnalyzer?.type
                      )
                    : editedSnapshot?.manual_sweep_time || ''
                }
                onChange={handleChange}
                disabled={editedSnapshot?.sweep_time_mode !== 1}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    );
  }
);

export default AnalyzerSnapshotsSettings;
